export const SUPPORTED_LANGUAGES = [
  { key: "en-US", label: "English", flagCode: "us" },
  { key: "de-DE", label: "Deutsch", flagCode: "de" },
  { key: "nl-NL", label: "Nederlands", flagCode: "nl" },
  { key: "cs-CZ", label: "Čeština", flagCode: "cz" },
  { key: "pl-PL", label: "Polski", flagCode: "pl" },
  { key: "ru-RU", label: "Русский", flagCode: "ru" }
] as const;

export type LanguageKey = typeof SUPPORTED_LANGUAGES[number]["key"];

export const TRANSLATION_KEYS = [
  "app.title",
  "app.titleCheckin",
  "app.titleCheckout",
  "actions.back",
  "actions.continue",
  "actions.confirm",
  "actions.finish",
  "actions.startOver",
  "actions.retry",
  "actions.clear",
  "actions.cancel",
  "language.title",
  "language.subtitle",
  "mode.title",
  "mode.description",
  "mode.checkin",
  "mode.checkout",
  "checkin.batch.title",
  "checkin.batch.subtitle",
  "checkin.batch.placeholder",
  "checkin.batch.error",
  "checkin.license.title",
  "checkin.license.subtitle",
  "checkin.license.placeholder",
  "checkin.supplier.title",
  "checkin.supplier.subtitle",
  "checkin.supplier.placeholder",
  "checkin.carrier.title",
  "checkin.carrier.subtitle",
  "checkin.carrier.nameLabel",
  "checkin.carrier.contactLabel",
  "checkin.assortment.title",
  "checkin.assortment.subtitle",
  "checkin.wood.title",
  "checkin.wood.subtitle",
  "checkin.wood.placeholder",
  "checkin.waste.title",
  "checkin.waste.subtitle",
  "checkin.waste.keyLabel",
  "checkin.waste.locationLabel",
  "checkin.weight.waitingTitle",
  "checkin.weight.waitingMessage",
  "checkin.weight.resultTitle",
  "checkin.weight.resultSubtitle",
  "checkin.success.title",
  "checkin.success.subtitle",
  "checkin.success.destinationLabel",
  "checkout.batch.title",
  "checkout.batch.subtitle",
  "checkout.license.title",
  "checkout.license.subtitle",
  "checkout.weight.waitingTitle",
  "checkout.weight.waitingMessage",
  "checkout.weight.resultTitle",
  "checkout.signature.title",
  "checkout.signature.subtitle",
  "checkout.signature.clear",
  "checkout.signature.error",
  "checkout.success.title",
  "checkout.success.subtitle",
  "checkout.receipt.title",
  "checkout.receipt.subtitle",
  "status.transportNotFound",
  "status.transportLookupFailed",
  "status.autoReset",
  "status.weightConfirmationMissingBatch",
  "status.weightConfirmationError",
  "status.weightConfirmationSuperseded",
  "status.weightConfirmationNotFound",
  "status.weightConfirmationAwaitingYard",
  "status.weightConfirmationApproved",
  "status.weightConfirmationHidden",
  "redirect.preCheckinTitle",
  "redirect.preCheckinMessage",
  "redirect.confirm",
  "redirect.countdown",
  "validation.required",
  "weight.label",
  "weight.unit",
  "weight.followUp",
  "weight.popup.title",
  "weight.popup.subtitle",
  "weight.popup.confirm",
  "app.backToStart"
] as const;

export type TranslationKey = typeof TRANSLATION_KEYS[number];
export type TranslationMap = Record<TranslationKey, string>;

const baseTranslations: TranslationMap = {
  "app.title": "Driver Check-In / Check-Out",
  "app.titleCheckin": "CHECK-IN",
  "app.titleCheckout": "CHECK-OUT",
  "actions.back": "Back",
  "actions.continue": "Continue",
  "actions.confirm": "Confirm",
  "actions.finish": "Finish",
  "actions.startOver": "Start over",
  "actions.retry": "Try again",
  "actions.clear": "Clear",
  "actions.cancel": "Cancel",
  "language.title": "Choose your language",
  "language.subtitle": "We will guide you through the process in your preferred language.",
  "mode.title": "What do you want to do?",
  "mode.description": "Select the process you would like to start.",
  "mode.checkin": "CHECK-IN",
  "mode.checkout": "CHECK-OUT",
  "checkin.batch.title": "Enter transport batch number",
  "checkin.batch.subtitle": "Please type the transport batch number from your documents.",
  "checkin.batch.placeholder": "Transport batch number",
  "checkin.batch.error": "The transport batch number could not be found. Please check the entry.",
  "checkin.license.title": "Confirm license plate",
  "checkin.license.subtitle": "Adjust the license plate if necessary and continue.",
  "checkin.license.placeholder": "License plate",
  "checkin.supplier.title": "Supplier Document Number",
  "checkin.supplier.subtitle": "Please enter the supplier document number.",
  "checkin.supplier.placeholder": "Supplier document number",
  "checkin.carrier.title": "Carrier information",
  "checkin.carrier.subtitle": "Provide optional contact details for your carrier.",
  "checkin.carrier.nameLabel": "Carrier name",
  "checkin.carrier.contactLabel": "Contact (phone / email)",
  "checkin.assortment.title": "Assortment Distribution",
  "checkin.assortment.subtitle": "Please assign the delivered assortments to staging numbers. The total distribution must equal 100%.",
  "checkin.wood.title": "Assortment distribution",
  "checkin.wood.subtitle": "Describe the wood assortments you deliver (species, percentages, notes).",
  "checkin.wood.placeholder": "Example: 70% spruce, 30% pine",
  "checkin.waste.title": "Waste key and loading location",
  "checkin.waste.subtitle": "Enter or verify the waste key and the loading location.",
  "checkin.waste.keyLabel": "Waste key",
  "checkin.waste.locationLabel": "Loading location",
  "checkin.weight.waitingTitle": "Waiting for weighing",
  "checkin.weight.waitingMessage": "Please remain on the scale. We will display the result shortly.",
  "checkin.weight.resultTitle": "Weight result",
  "checkin.weight.resultSubtitle": "Please confirm the displayed weight to continue.",
  "checkin.success.title": "Check-In completed",
  "checkin.success.subtitle": "Thank you. Please proceed to the indicated unloading position.",
  "checkin.success.destinationLabel": "Unloading position",
  "checkout.batch.title": "Enter transport batch number",
  "checkout.batch.subtitle": "Provide the batch number to continue with the check-out.",
  "checkout.license.title": "Select license plate",
  "checkout.license.subtitle": "Confirm your vehicle's license plate.",
  "checkout.weight.waitingTitle": "Waiting for weighing",
  "checkout.weight.waitingMessage": "Stay on the scale until the result appears.",
  "checkout.weight.resultTitle": "Weight result",
  "checkout.signature.title": "Sign the result",
  "checkout.signature.subtitle": "Sign on the terminal to approve the weighing.",
  "checkout.signature.clear": "Clear signature",
  "checkout.signature.error": "Please provide a signature before continuing.",
  "checkout.success.title": "Check-Out completed",
  "checkout.success.subtitle": "You may now leave the yard. Drive carefully!",
  "checkout.receipt.title": "Delivery receipt",
  "checkout.receipt.subtitle": "The receipt is sent to SAP and printed at the gate.",
  "status.transportNotFound": "No transport with this number was found.",
  "status.transportLookupFailed": "The transport could not be validated. Please try again.",
  "status.autoReset": "Returning to start in {seconds} seconds...",
  "status.weightConfirmationMissingBatch": "No transport number available. Please go back and enter the batch number.",
  "status.weightConfirmationError": "We could not reach the yard confirmation service. Please contact the staff.",
  "status.weightConfirmationSuperseded": "Another confirmation request replaced this one. Please restart the process.",
  "status.weightConfirmationNotFound": "The confirmation request no longer exists. Please restart the process.",
  "status.weightConfirmationAwaitingYard": "Waiting for yard confirmation. Please remain on the scale.",
  "status.weightConfirmationApproved": "Yard has approved the weight. You may continue.",
  "status.weightConfirmationHidden": "Weight will appear once the yard confirms the result.",
  "redirect.preCheckinTitle": "Please Visit the Guard",
  "redirect.preCheckinMessage": "This transport requires pre-check-in. Please go to the guard station.",
  "redirect.confirm": "Go to Guard",
  "redirect.countdown": "Resetting in {seconds} seconds...",
  "validation.required": "This field is required.",
  "weight.label": "Net weight",
  "weight.unit": "kg",
  "weight.followUp": "Please wait for further instructions.",
  "weight.popup.title": "Confirm measured weight",
  "weight.popup.subtitle": "The scale has measured the following weight. Please check and confirm.",
  "weight.popup.confirm": "Confirm weight",
  "app.backToStart": "Back to start"
};

function buildTranslations(overrides: Partial<Record<TranslationKey, string>>): TranslationMap {
  return { ...baseTranslations, ...overrides };
}

export const translations: Record<LanguageKey, TranslationMap> = {
  "en-US": baseTranslations,
  "de-DE": buildTranslations({
    "app.title": "Fahrer Check-In / Check-Out",
    "app.titleCheckin": "CHECK-IN",
    "app.titleCheckout": "CHECK-OUT",
    "actions.back": "Zurück",
    "actions.continue": "Weiter",
    "actions.confirm": "Bestätigen",
    "actions.finish": "Abschließen",
    "actions.startOver": "Neu starten",
    "actions.retry": "Erneut versuchen",
    "actions.clear": "Zurücksetzen",
    "actions.cancel": "Abbrechen",
    "language.title": "Bitte Sprache auswählen",
    "language.subtitle": "Wir begleiten dich in deiner gewünschten Sprache durch den Prozess.",
    "mode.title": "Was möchtest du tun?",
    "mode.description": "Wähle den Prozess, den du starten möchtest.",
    "mode.checkin": "CHECK-IN",
    "mode.checkout": "CHECK-OUT",
    "checkin.batch.title": "Transportchargennummer eingeben",
    "checkin.batch.subtitle": "Bitte gib die Transportchargennummer aus deinen Unterlagen ein.",
    "checkin.batch.placeholder": "Transportchargennummer",
    "checkin.batch.error": "Die Transportchargennummer wurde nicht gefunden. Bitte Eingabe prüfen.",
    "checkin.license.title": "Kennzeichen bestätigen",
    "checkin.license.subtitle": "Passe das Kennzeichen bei Bedarf an und fahre fort.",
    "checkin.license.placeholder": "Kennzeichen",
    "checkin.supplier.title": "Lieferantenbelegnummer",
    "checkin.supplier.subtitle": "Bitte geben Sie die Lieferantenbelegnummer ein.",
    "checkin.supplier.placeholder": "Lieferantenbelegnummer",
    "checkin.carrier.title": "Speditionsdaten",
    "checkin.carrier.subtitle": "Optional Kontaktdaten deiner Spedition angeben.",
    "checkin.carrier.nameLabel": "Name der Spedition",
    "checkin.carrier.contactLabel": "Kontakt (Telefon / E-Mail)", "checkin.assortment.title": "Sortimentsverteilung",
    "checkin.assortment.subtitle": "Bitte ordnen Sie die gelieferten Sortimente den Bereitstellungsnummern zu. Die Gesamtverteilung muss 100% ergeben.", "checkin.wood.title": "Sortimentsverteilung",
    "checkin.wood.subtitle": "Beschreibe die Holzsortimente deiner Ladung (Art, Anteil, Hinweise).",
    "checkin.wood.placeholder": "Beispiel: 70% Fichte, 30% Kiefer",
    "checkin.waste.title": "Abfallschlüssel und Ladeort",
    "checkin.waste.subtitle": "Bitte Abfallschlüssel und Ladeort prüfen oder eingeben.",
    "checkin.waste.keyLabel": "Abfallschlüssel",
    "checkin.waste.locationLabel": "Ladeort",
    "checkin.weight.waitingTitle": "Warten auf Verwiegung",
    "checkin.weight.waitingMessage": "Bitte auf der Waage bleiben. Das Ergebnis erscheint in Kürze.",
    "checkin.weight.resultTitle": "Wiegeergebnis",
    "checkin.weight.resultSubtitle": "Bitte bestätige das angezeigte Gewicht, um fortzufahren.",
    "checkin.success.title": "Check-In erfolgreich",
    "checkin.success.subtitle": "Vielen Dank. Fahre jetzt zur angegebenen Abladestelle.",
    "checkin.success.destinationLabel": "Abladeort",
    "checkout.batch.title": "Transportchargennummer eingeben",
    "checkout.batch.subtitle": "Bitte die Transportchargennummer für den Check-Out eingeben.",
    "checkout.license.title": "Kennzeichen auswählen",
    "checkout.license.subtitle": "Bestätige das Kennzeichen deines Fahrzeugs.",
    "checkout.weight.waitingTitle": "Warten auf Verwiegung",
    "checkout.weight.waitingMessage": "Bitte auf der Waage bleiben, bis das Ergebnis angezeigt wird.",
    "checkout.weight.resultTitle": "Wiegeergebnis",
    "checkout.signature.title": "Wiegeergebnis unterschreiben",
    "checkout.signature.subtitle": "Bitte auf dem Terminal unterschreiben, um das Ergebnis zu bestätigen.",
    "checkout.signature.clear": "Unterschrift löschen",
    "checkout.signature.error": "Bitte unterschreibe, bevor du fortfährst.",
    "checkout.success.title": "Check-Out erfolgreich",
    "checkout.success.subtitle": "Du kannst den Hof jetzt verlassen. Gute Fahrt!",
    "checkout.receipt.title": "Anlieferungsquittung",
    "checkout.receipt.subtitle": "Die Quittung wird in SAP erzeugt und am Pförtner gedruckt.",
    "status.transportNotFound": "Keine Transportdaten zu dieser Nummer gefunden.",
    "status.transportLookupFailed": "Transport konnte nicht geprüft werden. Bitte erneut versuchen.",
    "status.autoReset": "Automatischer Rücksprung in {seconds} Sekunden...",
    "status.weightConfirmationMissingBatch": "Keine Transportnummer vorhanden. Bitte zurückgehen und die Nummer eingeben.",
    "status.weightConfirmationError": "Hofbestätigung konnte nicht abgerufen werden. Bitte Personal kontaktieren.",
    "status.weightConfirmationSuperseded": "Eine andere Anfrage hat diese Bestätigung ersetzt. Bitte Prozess neu starten.",
    "status.weightConfirmationNotFound": "Die Bestätigungsanfrage existiert nicht mehr. Bitte neu starten.",
    "status.weightConfirmationAwaitingYard": "Warten auf Hofbestätigung. Bitte auf der Waage bleiben.",
    "status.weightConfirmationApproved": "Der Hof hat das Gewicht bestätigt. Du kannst fortfahren.",
    "status.weightConfirmationHidden": "Das Gewicht wird angezeigt, sobald der Hof das Ergebnis bestätigt.", "redirect.preCheckinTitle": "Bitte melden Sie sich beim Pförtner",
    "redirect.preCheckinMessage": "Dieser Transport erfordert einen Pre-Check-In. Bitte gehen Sie zum Pförtner.",
    "redirect.confirm": "Zum Pförtner",
    "redirect.countdown": "Zurücksetzen in {seconds} Sekunden...", "validation.required": "Dieses Feld ist erforderlich.",
    "weight.label": "Nettogewicht",
    "weight.unit": "kg",
    "weight.followUp": "Bitte auf weitere Anweisungen warten.",
    "weight.popup.title": "Gemessenes Gewicht bestätigen",
    "weight.popup.subtitle": "Die Waage hat folgendes Gewicht gemessen. Bitte prüfen und bestätigen.",
    "weight.popup.confirm": "Gewicht bestätigen",
    "app.backToStart": "Zur Startseite"
  }),
  "nl-NL": buildTranslations({
    "app.title": "Chauffeur Check-In / Check-Out",
    "app.titleCheckin": "CHECK-IN",
    "app.titleCheckout": "CHECK-OUT",
    "actions.back": "Terug",
    "actions.continue": "Doorgaan",
    "actions.confirm": "Bevestigen",
    "actions.finish": "Afronden",
    "actions.startOver": "Opnieuw starten",
    "actions.retry": "Opnieuw proberen",
    "actions.clear": "Wissen",
    "actions.cancel": "Annuleren",
    "language.title": "Kies je taal",
    "language.subtitle": "We begeleiden je in je voorkeurs taal door het proces.",
    "mode.title": "Wat wil je doen?",
    "mode.description": "Selecteer het proces dat je wilt starten.",
    "mode.checkin": "CHECK-IN",
    "mode.checkout": "CHECK-OUT",
    "checkin.batch.title": "Voer transportbatchnummer in",
    "checkin.batch.subtitle": "Voer het transportbatchnummer uit je documenten in.",
    "checkin.batch.placeholder": "Transportbatchnummer",
    "checkin.batch.error": "Geen transport gevonden met dit nummer. Controleer de invoer.",
    "checkin.license.title": "Kenteken bevestigen",
    "checkin.license.subtitle": "Pas het kenteken aan indien nodig en ga verder.",
    "checkin.license.placeholder": "Kenteken",
    "checkin.supplier.title": "Leveranciersdocumentnummer",
    "checkin.supplier.subtitle": "Voer het leveranciersdocumentnummer in.",
    "checkin.supplier.placeholder": "Leveranciersdocumentnummer",
    "checkin.carrier.title": "Vervoerder gegevens",
    "checkin.carrier.subtitle": "Vul optioneel contactgegevens van de vervoerder in.",
    "checkin.carrier.nameLabel": "Naam vervoerder",
    "checkin.carrier.contactLabel": "Contact (telefoon / e-mail)",
    "checkin.assortment.title": "Sortimentsverdeling",
    "checkin.assortment.subtitle": "Wijs de geleverde sortimenten toe aan bereidstellingsnummers. De totale verdeling moet 100% bedragen.",
    "checkin.wood.title": "Sortimentsverdeling",
    "checkin.wood.subtitle": "Beschrijf de houtsoorten in de lading (soort, percentages, opmerkingen).",
    "checkin.wood.placeholder": "Voorbeeld: 70% spar, 30% den",
    "checkin.waste.title": "Afvalcode en laadlocatie",
    "checkin.waste.subtitle": "Controleer of voer de afvalcode en laadlocatie in.",
    "checkin.waste.keyLabel": "Afvalcode",
    "checkin.waste.locationLabel": "Laadlocatie",
    "checkin.weight.waitingTitle": "Wachten op weging",
    "checkin.weight.waitingMessage": "Blijf op de weegbrug. Het resultaat verschijnt zo.",
    "checkin.weight.resultTitle": "Weegresultaat",
    "checkin.weight.resultSubtitle": "Bevestig het weergegeven gewicht om verder te gaan.",
    "checkin.success.title": "Check-In afgerond",
    "checkin.success.subtitle": "Dank je. Ga naar de aangegeven losplaats.",
    "checkin.success.destinationLabel": "Losplaats",
    "checkout.batch.title": "Voer transportbatchnummer in",
    "checkout.batch.subtitle": "Voer het batchnummer in om de check-out te starten.",
    "checkout.license.title": "Kenteken kiezen",
    "checkout.license.subtitle": "Bevestig het kenteken van het voertuig.",
    "checkout.weight.waitingTitle": "Wachten op weging",
    "checkout.weight.waitingMessage": "Blijf op de weegbrug totdat het resultaat verschijnt.",
    "checkout.weight.resultTitle": "Weegresultaat",
    "checkout.signature.title": "Onderteken het resultaat",
    "checkout.signature.subtitle": "Onderteken op het terminal om het weegresultaat goed te keuren.",
    "checkout.signature.clear": "Handtekening wissen",
    "checkout.signature.error": "Zet een handtekening voordat je verder gaat.",
    "checkout.success.title": "Check-Out afgerond",
    "checkout.success.subtitle": "Je kunt het terrein verlaten. Goede reis!",
    "checkout.receipt.title": "Afleverbewijs",
    "checkout.receipt.subtitle": "Het bewijs wordt naar SAP gestuurd en bij de poort geprint.",
    "status.transportNotFound": "Geen transport met dit nummer gevonden.",
    "status.transportLookupFailed": "Transport kon niet worden gecontroleerd. Probeer opnieuw.",
    "status.autoReset": "Automatisch terug naar start over {seconds} seconden...",
    "status.weightConfirmationMissingBatch": "Geen transportnummer aanwezig. Ga terug en vul het nummer in.",
    "status.weightConfirmationError": "Hofbevestiging kon niet worden opgehaald. Neem contact op met het personeel.",
    "status.weightConfirmationSuperseded": "Een andere aanvraag heeft deze bevestiging vervangen. Start het proces opnieuw.",
    "status.weightConfirmationNotFound": "De bevestigingsaanvraag bestaat niet meer. Start opnieuw.",
    "status.weightConfirmationAwaitingYard": "Wachten op hofbevestiging. Blijf op de weegbrug staan.",
    "status.weightConfirmationApproved": "De hof heeft het gewicht bevestigd. Je kunt doorgaan.",
    "status.weightConfirmationHidden": "Het gewicht verschijnt zodra de yard het resultaat bevestigt.", "redirect.preCheckinTitle": "Ga naar de portier",
    "redirect.preCheckinMessage": "Dit transport vereist pre-check-in. Ga naar de portier.",
    "redirect.confirm": "Naar de portier",
    "redirect.countdown": "Opnieuw instellen over {seconds} seconden...", "validation.required": "Dit veld is verplicht.",
    "weight.label": "Netto gewicht",
    "weight.unit": "kg",
    "weight.followUp": "Wacht op verdere instructies.",
    "weight.popup.title": "Bevestig gemeten gewicht",
    "weight.popup.subtitle": "De weegbrug heeft het volgende gewicht gemeten. Controleer en bevestig.",
    "weight.popup.confirm": "Gewicht bevestigen",
    "app.backToStart": "Terug naar start"
  }),
  "cs-CZ": buildTranslations({
    "app.title": "Řidič Check-In / Check-Out",
    "app.titleCheckin": "CHECK-IN",
    "app.titleCheckout": "CHECK-OUT",
    "actions.back": "Zpět",
    "actions.continue": "Pokračovat",
    "actions.confirm": "Potvrdit",
    "actions.finish": "Dokončit",
    "actions.startOver": "Začít znovu",
    "actions.retry": "Zkusit znovu",
    "actions.clear": "Vymazat",
    "actions.cancel": "Zrušit",
    "language.title": "Vyberte jazyk",
    "language.subtitle": "Procesem vás provedeme ve zvoleném jazyce.",
    "mode.title": "Co chcete udělat?",
    "mode.description": "Vyberte proces, který chcete spustit.",
    "mode.checkin": "CHECK-IN",
    "mode.checkout": "CHECK-OUT",
    "checkin.batch.title": "Zadejte číslo přepravní šarže",
    "checkin.batch.subtitle": "Zadejte číslo přepravní šarže z vašich dokladů.",
    "checkin.batch.placeholder": "Číslo přepravní šarže",
    "checkin.batch.error": "Přeprava s tímto číslem nebyla nalezena. Zkontrolujte zadání.",
    "checkin.license.title": "Potvrďte registrační značku",
    "checkin.license.subtitle": "V případě potřeby upravte registrační značku a pokračujte.",
    "checkin.license.placeholder": "SPZ",
    "checkin.supplier.title": "Číslo dodávky dodavatele",
    "checkin.supplier.subtitle": "Zadejte číslo dodávky dodavatele.",
    "checkin.supplier.placeholder": "Číslo dodávky dodavatele",
    "checkin.carrier.title": "Údaje o dopravci",
    "checkin.carrier.subtitle": "Volitelně uveďte kontaktní údaje na dopravce.",
    "checkin.carrier.nameLabel": "Název dopravce",
    "checkin.carrier.contactLabel": "Kontakt (telefon / e-mail)", "checkin.assortment.title": "Rozložení sortimentů",
    "checkin.assortment.subtitle": "Přiřaďte dodané sortimenty k přípravným číslům. Celková distribuce musí být 100%.", "checkin.wood.title": "Rozložení sortimentů",
    "checkin.wood.subtitle": "Popište dřevěné sortimenty nákladu (druh, procenta, poznámky).",
    "checkin.wood.placeholder": "Příklad: 70 % smrk, 30 % borovice",
    "checkin.waste.title": "Odpadový klíč a místo nakládky",
    "checkin.waste.subtitle": "Zkontrolujte nebo zadejte odpadový klíč a místo nakládky.",
    "checkin.waste.keyLabel": "Odpadový klíč",
    "checkin.waste.locationLabel": "Místo nakládky",
    "checkin.weight.waitingTitle": "Čekání na zvážení",
    "checkin.weight.waitingMessage": "Zůstaňte na váze. Výsledek se brzy zobrazí.",
    "checkin.weight.resultTitle": "Výsledek vážení",
    "checkin.weight.resultSubtitle": "Potvrďte zobrazenou hmotnost pro pokračování.",
    "checkin.success.title": "Check-In dokončen",
    "checkin.success.subtitle": "Děkujeme. Pokračujte na určené místo vykládky.",
    "checkin.success.destinationLabel": "Vykládací místo",
    "checkout.batch.title": "Zadejte číslo přepravní šarže",
    "checkout.batch.subtitle": "Zadejte číslo šarže pro pokračování v check-outu.",
    "checkout.license.title": "Vyberte registrační značku",
    "checkout.license.subtitle": "Potvrďte registrační značku vozidla.",
    "checkout.weight.waitingTitle": "Čekání na zvážení",
    "checkout.weight.waitingMessage": "Zůstaňte na váze, dokud se nezobrazí výsledek.",
    "checkout.weight.resultTitle": "Výsledek vážení",
    "checkout.signature.title": "Podepište výsledek",
    "checkout.signature.subtitle": "Podepište se na terminálu pro potvrzení výsledku vážení.",
    "checkout.signature.clear": "Smazat podpis",
    "checkout.signature.error": "Před pokračováním zadejte podpis.",
    "checkout.success.title": "Check-Out dokončen",
    "checkout.success.subtitle": "Můžete opustit areál. Šťastnou cestu!",
    "checkout.receipt.title": "Potvrzení o dodávce",
    "checkout.receipt.subtitle": "Potvrzení je odesláno do SAP a vytištěno na vrátnici.",
    "status.transportNotFound": "Přeprava s tímto číslem nebyla nalezena.",
    "status.transportLookupFailed": "Přepravu se nepodařilo ověřit. Zkuste to prosím znovu.",
    "status.autoReset": "Návrat na začátek za {seconds} sekund...",
    "status.weightConfirmationMissingBatch": "Chybí číslo přepravy. Vraťte se a zadejte ho prosím.",
    "status.weightConfirmationError": "Nepodařilo se získat potvrzení z dvora. Kontaktujte obsluhu.",
    "status.weightConfirmationSuperseded": "Tuto žádost nahradila jiná. Spusťte proces znovu.",
    "status.weightConfirmationNotFound": "Žádost o potvrzení již neexistuje. Spusťte znovu.",
    "status.weightConfirmationAwaitingYard": "Čeká se na potvrzení z dvora. Zůstaňte na váze.",
    "status.weightConfirmationApproved": "Dvůr potvrdil hmotnost. Můžete pokračovat.",
    "status.weightConfirmationHidden": "Hmotnost se zobrazí po potvrzení z dvora.",
    "redirect.preCheckinTitle": "Navštivte vrátnici",
    "redirect.preCheckinMessage": "Tento transport vyžaduje předběžnou registraci. Jděte k vrátnici.",
    "redirect.confirm": "Jít k vrátnici",
    "redirect.countdown": "Resetování za {seconds} sekund...",
    "validation.required": "Toto pole je povinné.",
    "weight.label": "Čistá hmotnost",
    "weight.unit": "kg",
    "weight.followUp": "Vyčkejte prosím na další pokyny.",
    "weight.popup.title": "Potvrďte naměřenou hmotnost",
    "weight.popup.subtitle": "Váha naměřila následující hmotnost. Zkontrolujte a potvrďte.",
    "weight.popup.confirm": "Potvrdit hmotnost",
    "app.backToStart": "Zpět na začátek"
  }),
  "pl-PL": buildTranslations({
    "app.title": "Kierowca Check-In / Check-Out",
    "app.titleCheckin": "CHECK-IN",
    "app.titleCheckout": "CHECK-OUT",
    "actions.back": "Wstecz",
    "actions.continue": "Dalej",
    "actions.confirm": "Potwierdź",
    "actions.finish": "Zakończ",
    "actions.startOver": "Rozpocznij od nowa",
    "actions.retry": "Spróbuj ponownie",
    "actions.clear": "Wyczyść",
    "actions.cancel": "Anuluj",
    "language.title": "Wybierz język",
    "language.subtitle": "Przeprowadzimy Cię przez proces w wybranym języku.",
    "mode.title": "Co chcesz zrobić?",
    "mode.description": "Wybierz proces, który chcesz rozpocząć.",
    "mode.checkin": "CHECK-IN",
    "mode.checkout": "CHECK-OUT",
    "checkin.batch.title": "Wprowadź numer partii transportu",
    "checkin.batch.subtitle": "Podaj numer partii z dokumentów.",
    "checkin.batch.placeholder": "Numer partii transportu",
    "checkin.batch.error": "Nie znaleziono transportu o tym numerze. Sprawdź wpis.",
    "checkin.license.title": "Potwierdź tablicę rejestracyjną",
    "checkin.license.subtitle": "W razie potrzeby popraw tablicę i kontynuuj.",
    "checkin.license.placeholder": "Tablica rejestracyjna",
    "checkin.supplier.title": "Numer dokumentu dostawcy",
    "checkin.supplier.subtitle": "Wprowadź numer dokumentu dostawcy.",
    "checkin.supplier.placeholder": "Numer dokumentu dostawcy",
    "checkin.carrier.title": "Dane przewoźnika",
    "checkin.carrier.subtitle": "Podaj opcjonalnie dane kontaktowe przewoźnika.",
    "checkin.carrier.nameLabel": "Nazwa przewoźnika",
    "checkin.carrier.contactLabel": "Kontakt (telefon / e-mail)",
    "checkin.assortment.title": "Rozkład sortymentów",
    "checkin.assortment.subtitle": "Przypisz dostarczone sortymenty do numerów przygotowania. Całkowity rozkład musi wynosić 100%.",
    "checkin.wood.title": "Rozkład sortymentów",
    "checkin.wood.subtitle": "Opisz sortymenty drewna (gatunek, procenty, uwagi).",
    "checkin.wood.placeholder": "Przykład: 70% świerk, 30% sosna",
    "checkin.waste.title": "Kod odpadu i miejsce załadunku",
    "checkin.waste.subtitle": "Sprawdź lub wprowadź kod odpadu i miejsce załadunku.",
    "checkin.waste.keyLabel": "Kod odpadu",
    "checkin.waste.locationLabel": "Miejsce załadunku",
    "checkin.weight.waitingTitle": "Oczekiwanie na ważenie",
    "checkin.weight.waitingMessage": "Pozostań na wadze. Wynik wkrótce się pojawi.",
    "checkin.weight.resultTitle": "Wynik ważenia",
    "checkin.weight.resultSubtitle": "Potwierdź wyświetloną masę, aby kontynuować.",
    "checkin.success.title": "Check-In zakończony",
    "checkin.success.subtitle": "Dziękujemy. Udaj się do wskazanego miejsca rozładunku.",
    "checkin.success.destinationLabel": "Miejsce rozładunku",
    "checkout.batch.title": "Wprowadź numer partii transportu",
    "checkout.batch.subtitle": "Podaj numer partii, aby kontynuować check-out.",
    "checkout.license.title": "Wybierz tablicę rejestracyjną",
    "checkout.license.subtitle": "Potwierdź tablicę swojego pojazdu.",
    "checkout.weight.waitingTitle": "Oczekiwanie na ważenie",
    "checkout.weight.waitingMessage": "Pozostań na wadze, aż pojawi się wynik.",
    "checkout.weight.resultTitle": "Wynik ważenia",
    "checkout.signature.title": "Podpisz wynik",
    "checkout.signature.subtitle": "Podpisz się na terminalu, aby zatwierdzić wynik ważenia.",
    "checkout.signature.clear": "Wyczyść podpis",
    "checkout.signature.error": "Podpisz przed kontynuacją.",
    "checkout.success.title": "Check-Out zakończony",
    "checkout.success.subtitle": "Możesz opuścić zakład. Szerokiej drogi!",
    "checkout.receipt.title": "Potwierdzenie dostawy",
    "checkout.receipt.subtitle": "Potwierdzenie jest wysyłane do SAP i drukowane przy bramie.",
    "status.transportNotFound": "Nie znaleziono transportu o tym numerze.",
    "status.transportLookupFailed": "Nie udało się zweryfikować transportu. Spróbuj ponownie.",
    "status.autoReset": "Powrót do startu za {seconds} sekund...",
    "status.weightConfirmationMissingBatch": "Brak numeru transportu. Wróć i wpisz numer.",
    "status.weightConfirmationError": "Nie udało się pobrać potwierdzenia z placu. Skontaktuj się z obsługą.",
    "status.weightConfirmationSuperseded": "Inna prośba zastąpiła to potwierdzenie. Uruchom proces ponownie.",
    "status.weightConfirmationNotFound": "Żądanie potwierdzenia już nie istnieje. Uruchom ponownie.",
    "status.weightConfirmationAwaitingYard": "Oczekiwanie na potwierdzenie z placu. Pozostań na wadze.",
    "status.weightConfirmationApproved": "Plac potwierdził wagę. Możesz kontynuować.",
    "status.weightConfirmationHidden": "Waga pojawi się po zatwierdzeniu przez plac.",
    "redirect.preCheckinTitle": "Zgłoś się do portiera",
    "redirect.preCheckinMessage": "Ten transport wymaga wstępnej rejestracji. Udaj się do portiera.",
    "redirect.confirm": "Idź do portiera",
    "redirect.countdown": "Resetowanie za {seconds} sekund...",
    "validation.required": "To pole jest wymagane.",
    "weight.label": "Masa netto",
    "weight.unit": "kg",
    "weight.followUp": "Poczekaj na dalsze instrukcje.",
    "weight.popup.title": "Potwierdź zmierzoną masę",
    "weight.popup.subtitle": "Waga zmierzyła następującą masę. Sprawdź i potwierdź.",
    "weight.popup.confirm": "Potwierdź wagę",
    "app.backToStart": "Powrót do startu"
  }),
  "ru-RU": buildTranslations({
    "app.title": "Регистрация въезда / выезда",
    "app.titleCheckin": "Регистрация въезда",
    "app.titleCheckout": "Регистрация выезда",
    "actions.back": "Назад",
    "actions.continue": "Далее",
    "actions.confirm": "Подтвердить",
    "actions.finish": "Завершить",
    "actions.startOver": "Начать заново",
    "actions.retry": "Повторить",
    "actions.clear": "Очистить",
    "actions.cancel": "Отменить",
    "language.title": "Выберите язык",
    "language.subtitle": "Мы проведём вас через процесс на выбранном языке.",
    "mode.title": "Что вы хотите сделать?",
    "mode.description": "Выберите процесс, который нужно запустить.",
    "mode.checkin": "CHECK-IN",
    "mode.checkout": "CHECK-OUT",
    "checkin.batch.title": "Введите номер партии",
    "checkin.batch.subtitle": "Введите номер транспортной партии из документов.",
    "checkin.batch.placeholder": "Номер партии",
    "checkin.batch.error": "Транспорт с таким номером не найден. Проверьте ввод.",
    "checkin.license.title": "Подтвердите номерной знак",
    "checkin.license.subtitle": "При необходимости исправьте номер и продолжайте.",
    "checkin.license.placeholder": "Номерной знак",
    "checkin.supplier.title": "Номер документа поставщика",
    "checkin.supplier.subtitle": "Введите номер документа поставщика.",
    "checkin.supplier.placeholder": "Номер документа поставщика",
    "checkin.carrier.title": "Данные перевозчика",
    "checkin.carrier.subtitle": "При необходимости укажите контактные данные перевозчика.",
    "checkin.carrier.nameLabel": "Название перевозчика",
    "checkin.carrier.contactLabel": "Контакт (телефон / e-mail)", "checkin.assortment.title": "Распределение сортимента",
    "checkin.assortment.subtitle": "Назначьте доставленные сортименты номерам подготовки. Общее распределение должно составлять 100%.", "checkin.wood.title": "Распределение сортимента",
    "checkin.wood.subtitle": "Опишите древесные сортименты груза (порода, проценты, примечания).",
    "checkin.wood.placeholder": "Пример: 70% ель, 30% сосна",
    "checkin.waste.title": "Код отходов и место погрузки",
    "checkin.waste.subtitle": "Проверьте или введите код отходов и место погрузки.",
    "checkin.waste.keyLabel": "Код отходов",
    "checkin.waste.locationLabel": "Место погрузки",
    "checkin.weight.waitingTitle": "Ожидание взвешивания",
    "checkin.weight.waitingMessage": "Оставайтесь на весах. Результат появится скоро.",
    "checkin.weight.resultTitle": "Результат взвешивания",
    "checkin.weight.resultSubtitle": "Подтвердите показанный вес, чтобы продолжить.",
    "checkin.success.title": "Check-In завершён",
    "checkin.success.subtitle": "Спасибо. Проследуйте к указанному месту разгрузки.",
    "checkin.success.destinationLabel": "Место разгрузки",
    "checkout.batch.title": "Введите номер партии",
    "checkout.batch.subtitle": "Укажите номер партии, чтобы продолжить Check-Out.",
    "checkout.license.title": "Выберите номерной знак",
    "checkout.license.subtitle": "Подтвердите номер вашего автомобиля.",
    "checkout.weight.waitingTitle": "Ожидание взвешивания",
    "checkout.weight.waitingMessage": "Оставайтесь на весах до появления результата.",
    "checkout.weight.resultTitle": "Результат взвешивания",
    "checkout.signature.title": "Подпишите результат",
    "checkout.signature.subtitle": "Подпишите на терминале для подтверждения результата.",
    "checkout.signature.clear": "Очистить подпись",
    "checkout.signature.error": "Пожалуйста, поставьте подпись перед продолжением.",
    "checkout.success.title": "Check-Out завершён",
    "checkout.success.subtitle": "Можете покинуть территорию. Счастливого пути!",
    "checkout.receipt.title": "Квитанция о поставке",
    "checkout.receipt.subtitle": "Квитанция отправляется в SAP и печатается на проходной.",
    "status.transportNotFound": "Транспорт с таким номером не найден.",
    "status.transportLookupFailed": "Не удалось проверить транспорт. Попробуйте снова.",
    "status.autoReset": "Возврат на старт через {seconds} секунд...",
    "status.weightConfirmationMissingBatch": "Номер рейса отсутствует. Вернитесь и введите номер.",
    "status.weightConfirmationError": "Не удалось получить подтверждение от двора. Обратитесь к персоналу.",
    "status.weightConfirmationSuperseded": "Этот запрос был заменён другим. Перезапустите процесс.",
    "status.weightConfirmationNotFound": "Запрос на подтверждение больше не найден. Перезапустите процесс.",
    "status.weightConfirmationAwaitingYard": "Ожидание подтверждения от двора. Пожалуйста, оставайтесь на весах.",
    "status.weightConfirmationApproved": "Двор подтвердил вес. Можно продолжить.",
    "status.weightConfirmationHidden": "Вес отобразится после подтверждения со двора.",
    "redirect.preCheckinTitle": "Обратитесь к охраннику",
    "redirect.preCheckinMessage": "Этот транспорт требует предварительной регистрации. Обратитесь к охраннику.",
    "redirect.confirm": "К охраннику",
    "redirect.countdown": "Сброс через {seconds} секунд...",
    "validation.required": "Это поле обязательно.",
    "weight.label": "Масса нетто",
    "weight.unit": "кг",
    "weight.followUp": "Дождитесь дальнейших указаний.",
    "weight.popup.title": "Подтвердите измеренный вес",
    "weight.popup.subtitle": "Весы измерили следующий вес. Проверьте и подтвердите.",
    "weight.popup.confirm": "Подтвердить вес",
    "app.backToStart": "Вернуться к началу"
  })
};

export function getTranslations(language: LanguageKey): TranslationMap {
  return translations[language] ?? baseTranslations;
}

export function resolveLanguageCandidate(language: string): LanguageKey {
  const normalized = SUPPORTED_LANGUAGES.find((candidate) => candidate.key === language);
  if (normalized) {
    return normalized.key;
  }

  const short = language.split("-")[0];
  const fallbackCandidate = SUPPORTED_LANGUAGES.find((candidate) => candidate.key.startsWith(short));
  return (fallbackCandidate?.key ?? "en-US") as LanguageKey;
}
